import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  IconButton,
  Chip,
  Dialog,
  TextField,
  InputAdornment
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FlashlightOnIcon from '@mui/icons-material/FlashlightOn';
import FlashlightOffIcon from '@mui/icons-material/FlashlightOff';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';

/**
 * Universal Barcode/QR Scanner Component
 * Supports: QR codes, EAN-13, EAN-8, UPC-A, UPC-E, Code-128, Code-39
 */
function BarcodeScanner() {
  const navigate = useNavigate();
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);
  const [torchOn, setTorchOn] = useState(false);
  const lastScanRef = useRef(null);
  const [scanType, setScanType] = useState('');
  const [manualOpen, setManualOpen] = useState(false);
  const [manualCode, setManualCode] = useState('');

  useEffect(() => {
    startScanner();
    return () => {
      stopScanner();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startScanner = async () => {
    try {
      setError(null);
      setScanning(true);

      // Initialize scanner with supported formats
      const formatsToSupport = [
        Html5QrcodeSupportedFormats.QR_CODE,
        Html5QrcodeSupportedFormats.EAN_13,
        Html5QrcodeSupportedFormats.EAN_8,
        Html5QrcodeSupportedFormats.UPC_A,
        Html5QrcodeSupportedFormats.UPC_E,
        Html5QrcodeSupportedFormats.CODE_128,
        Html5QrcodeSupportedFormats.CODE_39
      ];

      const html5QrCode = new Html5Qrcode("barcode-scanner", { formatsToSupport });
      html5QrCodeRef.current = html5QrCode;

      // Responsive QR box size based on screen width
      const screenWidth = window.innerWidth;
      const qrboxSize = screenWidth < 600 ? Math.min(screenWidth - 60, 250) : 250;

      const config = {
        fps: 10,
        qrbox: { width: qrboxSize, height: qrboxSize },
        aspectRatio: 1.0
      };

      // Start scanning
      await html5QrCode.start(
        { facingMode: "environment" }, // Use back camera
        config,
        onScanSuccess,
        onScanFailure
      );

      console.log('📸 Scanner started successfully');

    } catch (err) {
      console.error('❌ Scanner error:', err);
      setError('Impossibile avviare la fotocamera. Verifica i permessi.');
      setScanning(false);
    }
  };

  const stopScanner = async () => {
    if (html5QrCodeRef.current && html5QrCodeRef.current.isScanning) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current.clear();
        console.log('🛑 Scanner stopped');
      } catch (err) {
        console.error('Error stopping scanner:', err);
      }
    }
  };

  const onScanSuccess = (decodedText, decodedResult) => {
    // Avoid duplicate scans
    if (lastScanRef.current === decodedText) return;

    lastScanRef.current = decodedText;
    console.log('✅ Scan success:', decodedText);
    console.log('📊 Scan type:', decodedResult.result.format.formatName);

    setScanType(decodedResult.result.format.formatName);

    // Stop scanner
    stopScanner();

    // Determine if it's a QR code or barcode
    const isQR = decodedResult.result.format.formatName === 'QR_CODE';

    if (isQR) {
      // QR Code - could be a URL or product ID
      handleQRCode(decodedText);
    } else {
      // Barcode - search by barcode number
      handleBarcode(decodedText);
    }
  };

  const onScanFailure = (error) => {
    // Scanning failures are normal, ignore them
    // Only log occasionally to avoid console spam
    if (Math.random() < 0.01) {
      console.log('🔍 Scanning...');
    }
  };

  const handleQRCode = (qrData) => {
    try {
      // Check if it's a ProvaMi QR URL
      if (qrData.includes('provami-ai.web.app') || qrData.includes('localhost:3000')) {
        // Extract product ID from URL
        const url = new URL(qrData);
        const productId = url.searchParams.get('p') || url.searchParams.get('product');

        if (productId) {
          console.log('🏷️ ProvaMi QR detected, product ID:', productId);
          navigate(`/try?p=${productId}`);
        } else {
          setError('QR code non valido: nessun prodotto trovato');
        }
      } else if (qrData.startsWith('provami:')) {
        // Custom ProvaMi QR format: provami:product_id
        const productId = qrData.replace('provami:', '');
        console.log('🏷️ ProvaMi QR detected, product ID:', productId);
        navigate(`/try?p=${productId}`);
      } else {
        // Generic QR, try to use as product ID
        console.log('🔍 Generic QR, using as product ID:', qrData);
        navigate(`/try?p=${qrData}`);
      }
    } catch (err) {
      console.error('Error parsing QR:', err);
      setError('Errore nella lettura del QR code');
    }
  };

  const handleBarcode = (barcode) => {
    console.log('📊 Barcode detected:', barcode);
    // Navigate to product landing with barcode parameter
    navigate(`/try?barcode=${barcode}`);
  };

  const toggleTorch = async () => {
    try {
      if (html5QrCodeRef.current) {
        const capabilities = await html5QrCodeRef.current.getRunningTrackCapabilities();
        if (capabilities.torch) {
          await html5QrCodeRef.current.applyVideoConstraints({
            advanced: [{ torch: !torchOn }]
          });
          setTorchOn(!torchOn);
        } else {
          setError('Flash non disponibile su questo dispositivo');
        }
      }
    } catch (err) {
      console.error('Torch error:', err);
      setError('Impossibile attivare il flash');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative'
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: { xs: 1.5, sm: 2 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'rgba(0,0,0,0.3)',
          backdropFilter: 'blur(10px)',
          position: 'relative',
          zIndex: 10
        }}
      >
        <IconButton
          onClick={() => navigate(-1)}
          sx={{
            color: 'white',
            p: { xs: 1, sm: 1.5 }
          }}
        >
          <ArrowBackIcon />
        </IconButton>

        <Typography
          variant="h6"
          sx={{
            color: 'white',
            fontWeight: 700,
            letterSpacing: { xs: '1px', sm: '2px' },
            fontSize: { xs: '16px', sm: '20px' }
          }}
        >
          PROVAMI SCAN
        </Typography>

        <IconButton
          onClick={toggleTorch}
          sx={{
            color: 'white',
            p: { xs: 1, sm: 1.5 }
          }}
        >
          {torchOn ? <FlashlightOnIcon /> : <FlashlightOffIcon />}
        </IconButton>
      </Box>

      {/* Scanner Area */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 1.5, sm: 2, md: 3 },
          position: 'relative'
        }}
      >
        {/* Instructions */}
        <Paper
          elevation={3}
          sx={{
            p: { xs: 1.5, sm: 2 },
            mb: { xs: 2, sm: 3 },
            background: 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(10px)',
            maxWidth: { xs: '100%', sm: '400px' },
            width: '100%',
            mx: { xs: 1, sm: 0 }
          }}
        >
          <Typography
            variant="body1"
            align="center"
            gutterBottom
            sx={{ fontSize: { xs: '14px', sm: '16px' } }}
          >
            <strong>Inquadra un QR code o barcode</strong>
          </Typography>
          <Typography
            variant="body2"
            align="center"
            color="text.secondary"
            sx={{ fontSize: { xs: '12px', sm: '14px' } }}
          >
            Supporta QR ProvaMi ed etichette standard EAN/UPC
          </Typography>
        </Paper>

        {/* Scanner Container */}
        <Box
          sx={{
            maxWidth: { xs: '100%', sm: '500px' },
            width: '100%',
            position: 'relative',
            borderRadius: { xs: 1, sm: 2 },
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            mx: { xs: 1, sm: 0 }
          }}
        >
          <Box
            id="barcode-scanner"
            ref={scannerRef}
            sx={{
              width: '100%',
              '& video': {
                transform: 'none !important',
                WebkitTransform: 'none !important'
              }
            }}
          />

          {/* Scanning overlay */}
          {scanning && !error && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none'
              }}
            >
              {/* Scanning line animation */}
              <Box
                sx={{
                  position: 'absolute',
                  width: '80%',
                  height: '2px',
                  background: 'linear-gradient(90deg, transparent, #00ff88, transparent)',
                  animation: 'scan 2s ease-in-out infinite',
                  '@keyframes scan': {
                    '0%, 100%': { top: '20%' },
                    '50%': { top: '80%' }
                  }
                }}
              />
            </Box>
          )}
        </Box>

        {/* Format indicator */}
        {scanType && (
          <Chip
            label={`Formato: ${scanType}`}
            color="success"
            sx={{
              mt: { xs: 1.5, sm: 2 },
              fontSize: { xs: '12px', sm: '14px' }
            }}
          />
        )}

        {/* Error message */}
        {error && (
          <Alert
            severity="error"
            sx={{
              mt: { xs: 1.5, sm: 2 },
              maxWidth: { xs: '100%', sm: '400px' },
              width: '100%',
              mx: { xs: 1, sm: 0 },
              fontSize: { xs: '13px', sm: '14px' }
            }}
            action={
              <Button color="inherit" size="small" onClick={startScanner}>
                Riprova
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {/* Loading state */}
        {!scanning && !error && (
          <Box sx={{ mt: { xs: 1.5, sm: 2 }, textAlign: 'center' }}>
            <CircularProgress sx={{ color: 'white' }} size={40} />
            <Typography
              sx={{
                color: 'white',
                mt: 1,
                fontSize: { xs: '14px', sm: '16px' }
              }}
            >
              Caricamento scanner...
            </Typography>
          </Box>
        )}

        {/* Manual input option */}
        <Button
          variant="outlined"
          startIcon={<KeyboardIcon />}
          sx={{
            mt: { xs: 2, sm: 3 },
            mx: { xs: 1, sm: 0 },
            px: { xs: 3, sm: 4 },
            py: { xs: 1.25, sm: 1.5 },
            fontSize: { xs: '14px', sm: '15px' },
            fontWeight: 600,
            textTransform: 'none',
            color: 'white',
            borderColor: 'rgba(255, 255, 255, 0.5)',
            borderWidth: '2px',
            borderRadius: '14px',
            backdropFilter: 'blur(10px)',
            background: 'rgba(255, 255, 255, 0.08)',
            '&:hover': {
              borderColor: 'rgba(255, 255, 255, 0.8)',
              background: 'rgba(255, 255, 255, 0.15)',
              borderWidth: '2px'
            }
          }}
          onClick={() => setManualOpen(true)}
        >
          Inserisci codice manualmente
        </Button>
      </Box>

      {/* Tips */}
      <Paper
        sx={{
          m: { xs: 1.5, sm: 2 },
          p: { xs: 1.5, sm: 2 },
          background: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(10px)',
          borderRadius: '12px'
        }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          align="center"
          sx={{ fontSize: { xs: '12px', sm: '14px' } }}
        >
          Tieni il dispositivo fermo e assicurati che ci sia buona illuminazione
        </Typography>
      </Paper>

      {/* Manual Code Dialog */}
      <Dialog
        open={manualOpen}
        onClose={() => { setManualOpen(false); setManualCode(''); }}
        PaperProps={{
          sx: {
            borderRadius: '24px',
            maxWidth: '400px',
            width: '90%',
            m: 2,
            overflow: 'visible'
          }
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              backdropFilter: 'blur(8px)'
            }
          }
        }}
      >
        <Box sx={{ p: { xs: 3, sm: 4 }, position: 'relative' }}>
          {/* Close button */}
          <IconButton
            onClick={() => { setManualOpen(false); setManualCode(''); }}
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              color: '#999',
              '&:hover': { color: '#333', backgroundColor: 'rgba(0,0,0,0.05)' }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          {/* Icon */}
          <Box
            sx={{
              width: '56px',
              height: '56px',
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2.5
            }}
          >
            <KeyboardIcon sx={{ color: 'white', fontSize: '28px' }} />
          </Box>

          {/* Title */}
          <Typography
            sx={{
              fontSize: { xs: '20px', sm: '22px' },
              fontWeight: 800,
              color: '#1a1a1a',
              mb: 0.5
            }}
          >
            Inserisci codice
          </Typography>

          <Typography
            sx={{
              fontSize: '14px',
              color: '#888',
              mb: 3,
              lineHeight: 1.5
            }}
          >
            Digita il codice a barre o l'ID prodotto che trovi sull'etichetta del capo
          </Typography>

          {/* Input */}
          <TextField
            fullWidth
            autoFocus
            value={manualCode}
            onChange={(e) => setManualCode(e.target.value)}
            placeholder="Es. 8001234567890"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && manualCode.trim()) {
                setManualOpen(false);
                const code = manualCode.trim();
                setManualCode('');
                if (code.length > 20) {
                  handleQRCode(code);
                } else {
                  handleBarcode(code);
                }
              }
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#aaa' }} />
                  </InputAdornment>
                )
              }
            }}
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                borderRadius: '14px',
                fontSize: '16px',
                backgroundColor: '#f8f8f8',
                '&.Mui-focused': {
                  backgroundColor: '#fff'
                },
                '& fieldset': {
                  borderColor: '#e0e0e0',
                  borderWidth: '2px'
                },
                '&:hover fieldset': {
                  borderColor: '#667eea'
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#667eea'
                }
              }
            }}
          />

          {/* Submit button */}
          <Button
            fullWidth
            variant="contained"
            disabled={!manualCode.trim()}
            onClick={() => {
              setManualOpen(false);
              const code = manualCode.trim();
              setManualCode('');
              if (code.length > 20) {
                handleQRCode(code);
              } else {
                handleBarcode(code);
              }
            }}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              padding: '14px',
              fontSize: '16px',
              fontWeight: 700,
              borderRadius: '14px',
              textTransform: 'none',
              boxShadow: '0 6px 20px rgba(102, 126, 234, 0.35)',
              '&:hover': {
                background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                boxShadow: '0 8px 25px rgba(102, 126, 234, 0.45)'
              },
              '&.Mui-disabled': {
                background: '#e0e0e0',
                color: '#999',
                boxShadow: 'none'
              }
            }}
          >
            Cerca prodotto
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
}

export default BarcodeScanner;
